// TimerContext.tsx
import { useOpenPositionPairSpreadPercentage } from '@ui/features/Trade/hooks/useOpenPositionPairSpreadPercentage';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { useLeverageValue } from '@ui/features/Trade/store/useLeverage';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { getBalanceAfterGas } from '@ui/utils/getBalanceAfterGas';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import React, { createContext, useContext, useEffect, useMemo,useState  } from 'react';

import { useFromInput } from '../../../hooks/useFromInput';
import { useGetPayableTokenBalance } from '../../../hooks/useGetPayableTokenBalance';
import { useEntryPriceSnapshot } from '../hooks/useEntryPrice';
import { useEstOpenPositionGasFee } from '../hooks/useEstOpenPositionGasFee';
import { useMaxInput } from '../hooks/useMaxInput';
import { useMinInput } from '../hooks/useMinInput';
import { useCollateralToken } from '../store/useCollateralToken';
import { useIsLong } from '../store/useIsLong';
import { validateCollateralAmount } from '../utils/validateCollateralAmount';

export const useNewCollateralInputValidation = () => {
    const inputValue = useFromInput((s) => s.input).value;

    const tokenBalance = useGetPayableTokenBalance();
    const payToken = useCollateralToken((s) => s.useCollateralTokenValue());

    const payTokenPrice = usePayTokenPriceLatest(payToken);
    const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());
    const leverage = useLeverageValue((s) => s._persistValue);
    const isLong = useIsLong((s) => s._persistValue);

    const openPositionPairSpreadPercentage = useOpenPositionPairSpreadPercentage({
        isClosePrice: false,
    });

    const minInput = useMinInput({
        payTokenPrice,
        spreadPercentage: openPositionPairSpreadPercentage,
    });

    const maxInput = useMaxInput({
        payTokenPrice,
        leverage,
        pairInfos: currentTradePairInfo,
        spreadPercentage: openPositionPairSpreadPercentage,
    });

    const estimateOpenPositionGasFee = useEstOpenPositionGasFee({
        disabled: false,
    });

    const maxValue = useMemo(() => {
        return getBalanceAfterGas({
            balance: tokenBalance,
            gasFee: estimateOpenPositionGasFee?.gasInNativeToken,
            isNative: payToken?.isNative,
        });
    }, [estimateOpenPositionGasFee, payToken?.isNative, tokenBalance]);

    return useMemo(() => {
        if (isNil(inputValue.token) || isNil(payToken)) return;

        return validateCollateralAmount({
            ...inputValue,
            /**
             * inputValue.token in usd mode, the value can be :
             *
             * - 11654.99620986342427655101 // 20 decimals
             *
             * but balance , the value can be :
             *
             * - 11654.996209863424276551 // 18 decimals, base on pay token decimals
             *
             * inputValue.token > balance and causing the insufficient balance error
             *
             * to fix this, inputValue.token limit the decimals places to pay token balance and round floor
             *
             * 
             * inputValue.token.decimalPlaces(
                  payToken.decimals,
                  BigNumber.ROUND_FLOOR,
                )
              result is : 11654.99620986342427655 // 17 decimals, pay token decimals 18 -1
             */

            token: inputValue.token.decimalPlaces(
                payToken.decimals,
                BigNumber.ROUND_FLOOR,
            ),
            balance: maxValue,
            minInput,
            maxInput,
            isLong,
        });
    }, [inputValue, payToken, maxValue, minInput, maxInput, isLong]);
};